/* eslint-disable max-lines */
import cloneDeep from 'lodash.clonedeep';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { captureError } from '@smg-real-estate/browser-rum';

import type { SearchResult } from '@/api/insertions';
import {
  deleteInsertion as deleteInsertionApi,
  getUnifiedInsertionById,
  patchInsertionStatus,
  postInsertionArchive,
  postInsertionDuplication,
  postInsertionReactivate,
  postInsertionSubmittal,
  putInsertion,
  putPublishAddress,
  searchInsertions,
} from '@/api/insertions';
import { DeactivationReason } from '@/components/InsertionCard/ListingActionModals/modals/DeactivationModal/deactivationReason';
import { applyProjectBoostToListing } from '@/helpers/projectBoost';
import {
  searchFilterDefaults,
  type SearchControlData,
  type SearchFilterData,
  type SearchPaginationData,
} from '@/models/insertionsParameters';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import {
  ApiCallStatus,
  findInsertionById,
  getInitialInsertionsListState,
  getInitialInsertionsSearchState,
  type InsertionProcessingGoal,
} from '@/stores/types/insertions';
import { useUserStore } from '@/stores/user/useUserStore';
import { InsertionStatus, type Insertion, type SourcePlatform } from '@/types';

const INSERTION_POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 10000];

const INSERTION_WILL_BE_DEACTIVATED_TOAST = 'manageAdsPage.toasts.insertionWillBeDeactivated';

function findInsertionIndexById(insertions: Insertion[], id: string): number {
  return insertions.findIndex((insertion) => insertion.id === id);
}

function ensureInsertionDateTypes(insertion: Insertion): void {
  if (insertion.renewalPaymentDueDate) {
    insertion.renewalPaymentDueDate = new Date(insertion.renewalPaymentDueDate);
  }
}

function isInsertionProcessingComplete(
  status: InsertionStatus,
  goal: InsertionProcessingGoal,
): boolean {
  if (goal === 'archive') {
    return status === InsertionStatus.Archived;
  }

  if (goal === 'reactivate') {
    return status !== InsertionStatus.Republishing;
  }

  if (goal === 'publish') {
    return status === InsertionStatus.Published;
  }

  return false;
}

async function runInsertionProcessingPoll(
  params: {
    insertionId: string;
    userId: string;
    goal: InsertionProcessingGoal;
    sourcePlatform: SourcePlatform;
  },
  refetch: (
    userId: string,
    insertionId: string,
    sourcePlatform: SourcePlatform,
  ) => Promise<InsertionStatus | undefined>,
  onFinish: () => void,
): Promise<void> {
  let index = 0;
  let isComplete = false;

  while (index < INSERTION_POLL_DELAYS_MS.length && !isComplete) {
    await new Promise((resolve) => setTimeout(resolve, INSERTION_POLL_DELAYS_MS[index]));
    const status = await refetch(params.userId, params.insertionId, params.sourcePlatform);
    isComplete = status !== undefined && isInsertionProcessingComplete(status, params.goal);
    index += 1;
  }

  onFinish();
}

export const useInsertionsStore = defineStore(
  'insertions',
  // eslint-disable-next-line max-statements -- setup store returns state, computed, and side-effect functions together
  () => {
    const initial = getInitialInsertionsSearchState();
    const list = getInitialInsertionsListState();
    const insertionsChanged = ref(initial.insertionsChanged);
    const searchFilter = ref(initial.searchFilter);
    const searchFilterLatest = ref(initial.searchFilterLatest);
    const searchControl = ref(initial.searchControl);
    const searchPagination = ref(initial.searchPagination);
    const filtersWithCount = ref(initial.filtersWithCount);
    const insertions = ref(list.insertions);
    const apiCallStatus = ref(list.apiCallStatus);
    const apiCallInsertionId = ref(list.apiCallInsertionId);
    const insertionActionSuccessTitle = ref(list.insertionActionSuccessTitle);
    const insertionActionSuccessMessage = ref(list.insertionActionSuccessMessage);
    const archivedInsertionId = ref(list.archivedInsertionId);
    const deletedInsertionId = ref(list.deletedInsertionId);
    const search = ref(list.search);
    const processingInsertionIds = ref(list.processingInsertionIds);
    const processingInsertionGoals = ref(list.processingInsertionGoals);

    const apiCallInProgress = computed(() => apiCallStatus.value === ApiCallStatus.IN_PROGRESS);
    const apiCallFailed = computed(() => apiCallStatus.value === ApiCallStatus.ERROR);
    const apiCallSucceeded = computed(() => apiCallStatus.value === ApiCallStatus.SUCCESS);
    const isApiCallInProgressById = computed(() => !!apiCallInsertionId.value);
    const hasNextPage = computed(() =>
      Boolean(search.value.nextPageToFetch || search.value.nextInsertionStatusToFetch),
    );
    const insertionIdsWithProcessingPublish = computed(() =>
      processingInsertionIds.value.filter((id) => processingInsertionGoals.value[id] === 'publish'),
    );
    const insertionIdsWithProcessingArchive = computed(() =>
      processingInsertionIds.value.filter((id) => processingInsertionGoals.value[id] === 'archive'),
    );
    const insertionIdsWithProcessingReactivate = computed(() =>
      processingInsertionIds.value.filter(
        (id) => processingInsertionGoals.value[id] === 'reactivate',
      ),
    );

    const apiCallInProgressById = (insertionId: string): boolean =>
      !!apiCallInsertionId.value && apiCallInsertionId.value === insertionId;

    const setSearchFilter = (payload: SearchFilterData): void => {
      searchFilter.value = { ...searchFilter.value, ...payload };
    };

    const setSearchControl = (payload: SearchControlData): void => {
      searchControl.value = { ...searchControl.value, ...payload };
    };

    const setSearchPagination = (payload: SearchPaginationData): void => {
      searchPagination.value = { ...searchPagination.value, ...payload };
    };

    const resetSearchFilter = (): void => {
      searchFilter.value = { ...searchFilterDefaults };
      searchFilterLatest.value = { ...searchFilterDefaults };
    };

    const resetSearchState = (payload?: {
      searchFilter?: SearchFilterData;
      searchControl?: SearchControlData | Record<string, unknown>;
    }): void => {
      const next = getInitialInsertionsSearchState(payload?.searchFilter, payload?.searchControl);
      insertionsChanged.value = next.insertionsChanged;
      searchFilter.value = next.searchFilter;
      searchFilterLatest.value = next.searchFilterLatest;
      searchControl.value = next.searchControl;
      searchPagination.value = next.searchPagination;
      filtersWithCount.value = next.filtersWithCount;
    };

    const addInsertions = (payload: SearchResult): void => {
      payload.insertions.forEach(ensureInsertionDateTypes);
      insertions.value = [...insertions.value, ...payload.insertions];
      search.value = {
        ...search.value,
        nextPageToFetch: payload.nextPageCursor,
        nextInsertionStatusToFetch: payload.nextStatus as InsertionStatus,
      };
      insertionsChanged.value = false;
    };

    const updateInsertion = (updatedInsertion: Insertion): void => {
      const insertionIndex = findInsertionIndexById(insertions.value, updatedInsertion.id);
      if (insertionIndex < 0) {
        return;
      }

      const next = [...insertions.value];
      next[insertionIndex] = cloneDeep(updatedInsertion);
      insertions.value = next;
    };

    const upsertInsertion = (insertion: Insertion): void => {
      const insertionIndex = findInsertionIndexById(insertions.value, insertion.id);
      if (insertionIndex >= 0) {
        updateInsertion(insertion);
        return;
      }

      ensureInsertionDateTypes(insertion);
      insertions.value = [insertion, ...insertions.value];
    };

    const removeInsertion = (insertionId: string): void => {
      const insertionIndex = findInsertionIndexById(insertions.value, insertionId);
      if (insertionIndex < 0) {
        return;
      }

      const next = [...insertions.value];
      next.splice(insertionIndex, 1);
      insertions.value = next;
      insertionsChanged.value = true;
    };

    const updateInsertionStatus = (insertionId: string, status: InsertionStatus): void => {
      const insertion = findInsertionById(insertions.value, insertionId);
      if (!insertion) {
        return;
      }

      insertion.status = status;
      updateInsertion(insertion);
    };

    const registerInsertionProcessing = (insertionId: string): void => {
      if (processingInsertionIds.value.includes(insertionId)) {
        return;
      }

      processingInsertionIds.value = [...processingInsertionIds.value, insertionId];
    };

    const setInsertionProcessingGoal = (
      insertionId: string,
      goal: InsertionProcessingGoal,
    ): void => {
      processingInsertionGoals.value = {
        ...processingInsertionGoals.value,
        [insertionId]: goal,
      };
    };

    const finishInsertionProcessing = (insertionId: string): void => {
      processingInsertionIds.value = processingInsertionIds.value.filter(
        (id) => id !== insertionId,
      );
      const { [insertionId]: _removedGoal, ...remainingGoals } = processingInsertionGoals.value;
      processingInsertionGoals.value = remainingGoals;
    };

    const resetListState = (): void => {
      const next = getInitialInsertionsListState();
      insertions.value = next.insertions;
      apiCallStatus.value = next.apiCallStatus;
      apiCallInsertionId.value = next.apiCallInsertionId;
      insertionActionSuccessTitle.value = next.insertionActionSuccessTitle;
      insertionActionSuccessMessage.value = next.insertionActionSuccessMessage;
      archivedInsertionId.value = next.archivedInsertionId;
      deletedInsertionId.value = next.deletedInsertionId;
      search.value = next.search;
    };

    const setListingActionSuccess = (message: string | undefined): void => {
      insertionActionSuccessMessage.value = message;
    };

    const resetListingActionStatus = (): void => {
      insertionActionSuccessTitle.value = undefined;
      insertionActionSuccessMessage.value = undefined;
    };

    async function resolveUserIdForInsertion(insertionId: string): Promise<string | undefined> {
      const insertionFromList = insertions.value.find((item) => item.id === insertionId);
      const { useInsertionStore } = await import('@/stores/insertion/useInsertionStore');
      const funnelStore = useInsertionStore();
      const funnelUserId =
        funnelStore.getInsertionId === insertionId ? funnelStore.insertion.userId : undefined;

      return (
        insertionFromList?.userId ??
        funnelUserId ??
        useAgencyStore().activeAgencyPersonId ??
        (useUserStore().logonId || undefined)
      );
    }

    async function applyInsertionUpdate(insertion: Insertion): Promise<void> {
      upsertInsertion(insertion);

      const { useInsertionStore } = await import('@/stores/insertion/useInsertionStore');
      const funnelStore = useInsertionStore();
      if (funnelStore.getInsertionId === insertion.id) {
        funnelStore.assignInsertion(insertion);
      }

      const { useToplistingStore } = await import('@/stores/toplisting/useToplistingStore');
      const toplistingStore = useToplistingStore();

      if (toplistingStore.toplistingInsertionId === insertion.id) {
        toplistingStore.toplistingInsertion = insertion;
      }
    }

    async function refetchInsertionForPoll(
      userId: string,
      insertionId: string,
      sourcePlatform: SourcePlatform,
    ): Promise<InsertionStatus | undefined> {
      try {
        const updatedInsertion = await getUnifiedInsertionById(userId, insertionId, sourcePlatform);
        ensureInsertionDateTypes(updatedInsertion);
        await applyInsertionUpdate(updatedInsertion);

        return updatedInsertion.status;
      } catch (error) {
        captureError(error);
        return undefined;
      }
    }

    async function applyArchiveOptimisticUpdate(
      insertion: Insertion,
      isAgencyUser?: boolean,
    ): Promise<void> {
      updateInsertionStatus(insertion.id, InsertionStatus.Archiving);

      const { useInsertionStore } = await import('@/stores/insertion/useInsertionStore');
      const funnelStore = useInsertionStore();
      if (funnelStore.getInsertionId === insertion.id) {
        funnelStore.updateInsertionStatus(InsertionStatus.Archiving);
      }

      if (isAgencyUser) {
        setListingActionSuccess(INSERTION_WILL_BE_DEACTIVATED_TOAST);
      } else {
        insertionActionSuccessTitle.value = INSERTION_WILL_BE_DEACTIVATED_TOAST;
        setListingActionSuccess('manageAdsPage.toasts.confirmationEmailNotice');
      }
    }

    function registerInsertionProcessingWithGoal(
      insertionId: string,
      goal: InsertionProcessingGoal,
    ): void {
      registerInsertionProcessing(insertionId);
      setInsertionProcessingGoal(insertionId, goal);
      pollInsertionProcessingStatus(insertionId).catch((error) => {
        captureError(error);
      });
    }

    async function fetchFilteredInsertions(): Promise<void> {
      insertions.value = [];

      const requestData = {
        ...searchFilter.value,
        ...searchControl.value,
      };

      setSearchPagination({
        page: requestData.page,
      });

      const response = await searchInsertions(requestData, useUserStore().sourcePlatform);
      addInsertions({
        insertions: response.data,
        nextPageCursor: undefined,
      });

      setSearchPagination({
        pageSize: response.cursors?.pageSize,
        totalPages: response.cursors?.totalPages,
        totalItems: response.cursors?.totalCount,
      });
      filtersWithCount.value = response.filtersWithCount;
    }

    async function changeInsertionShouldHideAddress(payload: {
      insertion: Insertion;
      shouldHideAddress: boolean;
    }): Promise<void> {
      const { insertion, shouldHideAddress } = payload;

      try {
        apiCallStatus.value = ApiCallStatus.IN_PROGRESS;
        await putPublishAddress(
          insertion.userId,
          insertion.id,
          useUserStore().sourcePlatform,
          shouldHideAddress,
        );
        apiCallStatus.value = ApiCallStatus.SUCCESS;
      } catch (_) {
        apiCallStatus.value = ApiCallStatus.ERROR;
      }
    }

    async function reactivateInsertion(insertion: Insertion): Promise<void> {
      try {
        apiCallStatus.value = ApiCallStatus.IN_PROGRESS;

        const reactivatedInsertion = await postInsertionReactivate(
          insertion.userId,
          insertion.id,
          useUserStore().sourcePlatform,
        );

        ensureInsertionDateTypes(reactivatedInsertion);
        const mergedReactivatedInsertion: Insertion = {
          ...reactivatedInsertion,
          userId: reactivatedInsertion.userId ?? insertion.userId,
        };

        if (mergedReactivatedInsertion.id !== insertion.id) {
          removeInsertion(insertion.id);
        }
        upsertInsertion(mergedReactivatedInsertion);
        apiCallStatus.value = ApiCallStatus.SUCCESS;
        registerInsertionAfterReactivate(mergedReactivatedInsertion);
      } catch (error) {
        apiCallStatus.value = ApiCallStatus.ERROR;
        captureError(error);
      }
    }

    async function archiveInsertion(payload: {
      insertion: Insertion;
      archivalReasonKey: DeactivationReason;
      archivalReasonText: string;
      isAgencyUser?: boolean;
    }): Promise<void> {
      const { insertion, archivalReasonKey, archivalReasonText, isAgencyUser } = payload;

      apiCallStatus.value = ApiCallStatus.IN_PROGRESS;
      try {
        await postInsertionArchive(
          insertion.userId,
          insertion.id,
          archivalReasonKey,
          archivalReasonText,
          useUserStore().sourcePlatform,
        );
        await applyArchiveOptimisticUpdate(insertion, isAgencyUser);
        apiCallStatus.value = ApiCallStatus.SUCCESS;
        registerInsertionAfterArchive(insertion);
      } catch (_) {
        apiCallStatus.value = ApiCallStatus.ERROR;
      }
    }

    async function archiveDraftInsertion(payload: {
      insertion: Insertion;
      isAgencyUser?: boolean;
    }): Promise<void> {
      const { insertion, isAgencyUser } = payload;

      apiCallStatus.value = ApiCallStatus.IN_PROGRESS;
      try {
        await patchInsertionStatus(
          insertion.userId,
          insertion.id,
          InsertionStatus.Archived,
          useUserStore().sourcePlatform,
        );
        await applyArchiveOptimisticUpdate(insertion, isAgencyUser);
        apiCallStatus.value = ApiCallStatus.SUCCESS;
        registerInsertionAfterArchive(insertion);
      } catch (error) {
        apiCallStatus.value = ApiCallStatus.ERROR;
        captureError(error);
      }
    }

    async function deleteInsertion(insertion: Insertion): Promise<void> {
      apiCallStatus.value = ApiCallStatus.IN_PROGRESS;
      try {
        await deleteInsertionApi(insertion.userId, insertion.id, useUserStore().sourcePlatform);

        setListingActionSuccess('manageAdsPage.toasts.insertionSuccessfullyDeleted');
        apiCallStatus.value = ApiCallStatus.SUCCESS;
        await fetchFilteredInsertions();
      } catch (_) {
        apiCallStatus.value = ApiCallStatus.ERROR;
      }
    }

    async function duplicateInsertion(payload: { ownerId: string; id: string }): Promise<string> {
      const duplicatedInsertion = await postInsertionDuplication(
        payload.ownerId,
        payload.id,
        useUserStore().sourcePlatform,
      );
      insertionsChanged.value = true;
      return duplicatedInsertion.id;
    }

    async function refetchInsertionById(payload: {
      userId: string;
      insertionId: string;
    }): Promise<Insertion> {
      const { userId, insertionId } = payload;
      const insertion = await getUnifiedInsertionById(
        userId,
        insertionId,
        useUserStore().sourcePlatform,
      );

      ensureInsertionDateTypes(insertion);
      await applyInsertionUpdate(insertion);

      return insertion;
    }

    async function pollInsertionProcessingStatus(insertionId: string): Promise<void> {
      if (!processingInsertionIds.value.includes(insertionId)) {
        return;
      }

      const userId = await resolveUserIdForInsertion(insertionId);
      const finishProcessing = (): void => {
        finishInsertionProcessing(insertionId);
      };

      if (!userId) {
        finishProcessing();
        return;
      }

      await runInsertionProcessingPoll(
        {
          insertionId,
          userId,
          goal: processingInsertionGoals.value[insertionId] ?? 'publish',
          sourcePlatform: useUserStore().sourcePlatform,
        },
        refetchInsertionForPoll,
        finishProcessing,
      );
    }

    function registerInsertionAfterArchive(insertion: Insertion): void {
      if (insertion.status === InsertionStatus.Archived) {
        return;
      }

      registerInsertionProcessingWithGoal(insertion.id, 'archive');
    }

    function registerInsertionAfterReactivate(insertion: Insertion): void {
      if (insertion.status === InsertionStatus.Published) {
        return;
      }

      registerInsertionProcessingWithGoal(insertion.id, 'reactivate');
    }

    async function registerInsertionAfterPublish(insertion: Insertion): Promise<void> {
      upsertInsertion(insertion);

      if (insertion.status !== InsertionStatus.Published) {
        registerInsertionProcessingWithGoal(insertion.id, 'publish');
      }

      const { useInsertionStore } = await import('@/stores/insertion/useInsertionStore');
      const funnelStore = useInsertionStore();
      const funnelListing =
        funnelStore.getInsertionId === insertion.id ? funnelStore.getInsertionListing : undefined;

      const { useToplistingStore } = await import('@/stores/toplisting/useToplistingStore');

      useToplistingStore().trackToplistingFromFunnelListing({
        insertionId: insertion.id,
        listing: funnelListing ?? insertion.listing,
      });
    }

    /**
     * NC Boost lives on the listing rather than on the toplisting service, so booking or
     * cancelling it means writing `newConstructionData.projectType` and republishing.
     *
     * Returns the published insertion, or `undefined` when the update failed.
     */
    async function publishProjectBoostChange(payload: {
      insertion: Insertion;
      enabled: boolean;
    }): Promise<Insertion | undefined> {
      const { insertion, enabled } = payload;
      const sourcePlatform = useUserStore().sourcePlatform;

      apiCallStatus.value = ApiCallStatus.IN_PROGRESS;
      try {
        /*
         * The manage-ads list is filled from /insertions/search, which returns a reduced listing.
         * Putting that back would drop fields the API requires, so the full insertion is loaded
         * first and the project type is applied on top of it.
         */
        const currentInsertion: Insertion | undefined = await refetchInsertionById({
          userId: insertion.userId,
          insertionId: insertion.id,
        });
        const listing = applyProjectBoostToListing(
          currentInsertion?.listing ?? insertion.listing,
          enabled,
        );

        await putInsertion(insertion.userId, insertion.id, listing, sourcePlatform);
        const publishedInsertion = await postInsertionSubmittal(
          insertion.userId,
          insertion.id,
          sourcePlatform,
        );

        setListingActionSuccess(
          enabled
            ? 'manageAdsPage.toasts.projectBoostActivated'
            : 'manageAdsPage.toasts.projectBoostDeactivated',
        );
        apiCallStatus.value = ApiCallStatus.SUCCESS;

        return publishedInsertion;
      } catch (error) {
        apiCallStatus.value = ApiCallStatus.ERROR;
        captureError(error);

        return undefined;
      }
    }

    async function setProjectBoostAndPublish(payload: {
      insertion: Insertion;
      enabled: boolean;
    }): Promise<boolean> {
      const publishedInsertion = await publishProjectBoostChange(payload);

      if (!publishedInsertion) {
        return false;
      }

      await registerInsertionAfterPublish(publishedInsertion);

      return true;
    }

    return {
      insertionsChanged,
      searchFilter,
      searchFilterLatest,
      searchControl,
      searchPagination,
      filtersWithCount,
      insertions,
      apiCallStatus,
      apiCallInsertionId,
      insertionActionSuccessTitle,
      insertionActionSuccessMessage,
      archivedInsertionId,
      deletedInsertionId,
      search,
      processingInsertionIds,
      processingInsertionGoals,
      apiCallInProgress,
      apiCallFailed,
      apiCallSucceeded,
      isApiCallInProgressById,
      apiCallInProgressById,
      hasNextPage,
      insertionIdsWithProcessingPublish,
      insertionIdsWithProcessingArchive,
      insertionIdsWithProcessingReactivate,
      setSearchFilter,
      setSearchControl,
      resetSearchFilter,
      resetSearchState,
      addInsertions,
      updateInsertion,
      upsertInsertion,
      removeInsertion,
      updateInsertionStatus,
      finishInsertionProcessing,
      resetListState,
      setListingActionSuccess,
      resetListingActionStatus,
      fetchFilteredInsertions,
      changeInsertionShouldHideAddress,
      reactivateInsertion,
      archiveInsertion,
      archiveDraftInsertion,
      deleteInsertion,
      duplicateInsertion,
      refetchInsertionById,
      pollInsertionProcessingStatus,
      registerInsertionAfterArchive,
      registerInsertionAfterReactivate,
      registerInsertionAfterPublish,
      setProjectBoostAndPublish,
    };
  },
);
